import { Navigate } from "src/router";
import type { RouteObject } from "src/router";
import { lazyNamed, lazyWithRetry } from "src/utils/lazy-named";
import { FeatureFlagRoute } from "src/components/router/feature-flag-route";

const SettingsLoader = lazyWithRetry(() => import("./settings"));
const GeneralTab = lazyNamed(() => import("./components/data-tab"), "DataTab");
const DomainsTab = lazyNamed(
  () => import("./components/domains-tab"),
  "DomainsTab",
);
const ProvidersTab = lazyNamed(
  () => import("./components/providers-tab"),
  "ProvidersTab",
);
const ChannelsTab = lazyNamed(
  () => import("./components/channels-tab"),
  "ChannelsTab",
);
const AppsTab = lazyNamed(() => import("./components/apps-tab"), "AppsTab");
const AppDetail = lazyNamed(
  () => import("./components/app-detail"),
  "AppDetail",
);
const FrequencyCappingTab = lazyNamed(
  () => import("./components/frequency-capping-tab"),
  "FrequencyCappingTab",
);
const BrandGuidelinesTab = lazyNamed(
  () => import("./components/brand-guidelines-tab"),
  "BrandGuidelinesTab",
);
const ConsentTab = lazyNamed(
  () => import("./components/consent-tab"),
  "ConsentTab",
);
const SuppressionListsTab = lazyNamed(
  () => import("./components/suppression-lists-tab"),
  "SuppressionListsTab",
);
const SuppressionListDetail = lazyNamed(
  () => import("./components/suppression-lists/suppression-list-detail"),
  "SuppressionListDetail",
);
const SuppressionListDefinitionTab = lazyNamed(
  () => import("./components/suppression-lists/definition-tab"),
  "SuppressionListDefinitionTab",
);
const SuppressionListSubscriptionTopicsTab = lazyNamed(
  () => import("./components/suppression-lists/subscription-topics-tab"),
  "SuppressionListSubscriptionTopicsTab",
);
const FooterEditor = lazyNamed(
  () => import("./components/brand-guidelines/footer-editor"),
  "FooterEditor",
);
const LocalizationTab = lazyNamed(
  () => import("./components/localization-tab"),
  "LocalizationTab",
);

export const BRAND_GUIDELINES_PATH =
  "/native-delivery/settings/brand-guidelines";
export const FOOTER_EDITOR_PATH = `${BRAND_GUIDELINES_PATH}/footer`;
export const CHANNELS_PATH = "/native-delivery/settings/channels";
export const SETTINGS_GENERAL_PATH = "/native-delivery/settings/general";
export const FREQUENCY_CAPPING_PATH =
  "/native-delivery/settings/frequency-capping";

export const routes: RouteObject[] = [
  {
    path: "native-delivery/settings/apps/:appName",
    spacesAccess: "hidden-from-limited-view",
    element: <AppDetail />,
  },
  {
    path: "native-delivery/settings/suppression-lists/:id",
    spacesAccess: "hidden-from-limited-view",
    element: <SuppressionListDetail />,
    children: [
      {
        index: true,
        spacesAccess: "hidden-from-limited-view",
        element: <Navigate to="definition" replace />,
      },
      {
        path: "definition",
        spacesAccess: "hidden-from-limited-view",
        element: <SuppressionListDefinitionTab />,
      },
      {
        path: "subscription-topics",
        spacesAccess: "hidden-from-limited-view",
        element: <SuppressionListSubscriptionTopicsTab />,
      },
    ],
  },
  {
    // Sibling of the tabbed `native-delivery/settings` shell — the footer
    // editor is a full-page route with its own breadcrumb back to brand
    // guidelines, not a child tab.
    path: FOOTER_EDITOR_PATH.slice(1),
    spacesAccess: "hidden-from-limited-view",
    element: <FooterEditor />,
  },
  {
    path: "native-delivery/settings",
    spacesAccess: "hidden-from-limited-view",
    element: <SettingsLoader />,
    children: [
      {
        index: true,
        spacesAccess: "hidden-from-limited-view",
        element: <Navigate to="general" replace />,
      },
      {
        path: "general",
        spacesAccess: "hidden-from-limited-view",
        element: <GeneralTab />,
      },
      {
        path: "domains",
        spacesAccess: "hidden-from-limited-view",
        element: <DomainsTab />,
      },
      {
        path: "providers",
        spacesAccess: "hidden-from-limited-view",
        element: <ProvidersTab />,
      },
      {
        path: "channels",
        spacesAccess: "hidden-from-limited-view",
        element: <ChannelsTab />,
      },
      {
        path: "apps",
        spacesAccess: "hidden-from-limited-view",
        element: <AppsTab />,
      },
      {
        path: "frequency-capping",
        spacesAccess: "hidden-from-limited-view",
        element: <FrequencyCappingTab />,
      },
      {
        path: "brand-guidelines",
        spacesAccess: "hidden-from-limited-view",
        element: <BrandGuidelinesTab />,
      },
      {
        path: "consent",
        spacesAccess: "hidden-from-limited-view",
        element: <ConsentTab />,
      },
      {
        path: "suppression-lists",
        spacesAccess: "hidden-from-limited-view",
        element: <SuppressionListsTab />,
      },
      {
        path: "localization",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <FeatureFlagRoute
            flagKey="cepLocalization"
            redirect={SETTINGS_GENERAL_PATH}
          >
            <LocalizationTab />
          </FeatureFlagRoute>
        ),
      },
    ],
  },
];
